import React from 'react';
import theme from '../../theme';

/* ---- Card ---- */
export function Card({ children, style, onClick, ...props }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: theme.colors.bgCard,
        borderRadius: theme.radius,
        border: `1px solid ${theme.colors.border}`,
        padding: '20px',
        boxShadow: theme.shadow,
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

/* ---- Button ---- */
export function Button({ children, variant = 'primary', size = 'md', disabled, loading, style, ...props }) {
  const base = {
    border: 'none',
    borderRadius: theme.radiusSm,
    fontWeight: 600,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.6 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'background 0.15s, opacity 0.15s',
    fontFamily: theme.fonts.body,
  };
  const sizes = {
    sm: { padding: '6px 12px', fontSize: '13px' },
    md: { padding: '10px 20px', fontSize: '14px' },
    lg: { padding: '14px 28px', fontSize: '16px' },
  };
  const variants = {
    primary: { background: theme.colors.primary, color: '#fff' },
    accent: { background: theme.colors.accent, color: '#fff' },
    danger: { background: theme.colors.danger, color: '#fff' },
    outline: { background: 'transparent', color: theme.colors.primary, border: `1px solid ${theme.colors.primary}` },
    ghost: { background: 'transparent', color: theme.colors.textLight },
  };

  return (
    <button
      disabled={disabled || loading}
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}
      {...props}
    >
      {loading ? 'Loading...' : children}
    </button>
  );
}

/* ---- Input ---- */
export function Input({ label, error, style, ...props }) {
  return (
    <div style={{ marginBottom: '16px', ...style }}>
      {label && <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600, color: theme.colors.text }}>{label}</label>}
      <input
        style={{
          width: '100%',
          padding: '10px 12px',
          border: `1px solid ${error ? theme.colors.danger : theme.colors.border}`,
          borderRadius: theme.radiusSm,
          fontSize: '14px',
          fontFamily: theme.fonts.body,
          outline: 'none',
          boxSizing: 'border-box',
        }}
        {...props}
      />
      {error && <div style={{ color: theme.colors.danger, fontSize: '12px', marginTop: '4px' }}>{error}</div>}
    </div>
  );
}

/* ---- Select ---- */
export function Select({ label, children, error, style, ...props }) {
  return (
    <div style={{ marginBottom: '16px', ...style }}>
      {label && <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600, color: theme.colors.text }}>{label}</label>}
      <select
        style={{
          width: '100%',
          padding: '10px 12px',
          border: `1px solid ${error ? theme.colors.danger : theme.colors.border}`,
          borderRadius: theme.radiusSm,
          fontSize: '14px',
          fontFamily: theme.fonts.body,
          outline: 'none',
          background: '#fff',
          boxSizing: 'border-box',
        }}
        {...props}
      >
        {children}
      </select>
      {error && <div style={{ color: theme.colors.danger, fontSize: '12px', marginTop: '4px' }}>{error}</div>}
    </div>
  );
}

/* ---- Badge ---- */
export function Badge({ children, color = 'default', style }) {
  const colors = {
    default: { bg: theme.colors.bgAlt, color: theme.colors.textLight },
    success: { bg: '#C6F6D5', color: '#22543D' },
    warning: { bg: '#FEFCBF', color: '#744210' },
    danger: { bg: '#FED7D7', color: '#742A2A' },
    info: { bg: '#BEE3F8', color: '#2A4365' },
  };
  const c = colors[color] || colors.default;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.color,
      ...style,
    }}>
      {children}
    </span>
  );
}

/* ---- Status Badge (payment/order specific) ---- */
const STATUS_COLORS = {
  ACTIVE: 'success', SETTLED: 'success', SUBMITTED: 'info', ACCEPTED: 'info',
  VALIDATED: 'info', INITIATED: 'warning', PENDING_APPROVAL: 'warning', PAUSED: 'warning',
  FAILED: 'danger', REJECTED: 'danger', CANCELLED: 'danger', RETURNED: 'danger',
};
export function StatusBadge({ status }) {
  return <Badge color={STATUS_COLORS[status] || 'default'}>{status}</Badge>;
}

/* ---- Loading Spinner ---- */
export function Spinner({ size = 24 }) {
  return (
    <div style={{
      width: size, height: size,
      border: `3px solid ${theme.colors.border}`,
      borderTopColor: theme.colors.accent,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
      display: 'inline-block',
    }} />
  );
}

/* ---- Empty State ---- */
export function Empty({ message = 'No data' }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: theme.colors.textMuted }}>
      <div style={{ fontSize: '14px' }}>{message}</div>
    </div>
  );
}

/* ---- Money formatter ---- */
export function Money({ amount, currency = 'GBP', style }) {
  const formatted = new Intl.NumberFormat('en-GB', {
    style: 'currency', currency, minimumFractionDigits: 2,
  }).format(amount);
  return <span style={{ fontFamily: theme.fonts.mono, ...style }}>{formatted}</span>;
}

/* ---- Mask account number ---- */
export function MaskedAccount({ value, revealed, onToggle }) {
  if (!value) return <span>-</span>;
  const masked = value.length > 4 ? '****' + value.slice(-4) : value;
  return (
    <span
      onClick={onToggle}
      title={onToggle ? 'Click to toggle' : undefined}
      style={{ cursor: onToggle ? 'pointer' : 'default', fontFamily: theme.fonts.mono }}
    >
      {revealed ? value : masked}
    </span>
  );
}

/* ---- Mask sort code ---- */
export function MaskedSortCode({ value, revealed, onToggle }) {
  if (!value) return <span>-</span>;
  const formatted = value.replace(/(\d{2})(\d{2})(\d{2})/, '$1-$2-$3');
  const masked = 'XX-XX-' + value.slice(-2);
  return (
    <span
      onClick={onToggle}
      title={onToggle ? 'Click to toggle' : undefined}
      style={{ cursor: onToggle ? 'pointer' : 'default', fontFamily: theme.fonts.mono }}
    >
      {revealed ? formatted : masked}
    </span>
  );
}

/* ---- Pagination ---- */
export function Pagination({ page, pages, onChange }) {
  if (pages <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '20px', alignItems: 'center' }}>
      <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>Previous</Button>
      <span style={{ fontSize: '13px', color: theme.colors.textLight }}>Page {page} of {pages}</span>
      <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>Next</Button>
    </div>
  );
}

/* ---- Confirm Dialog ---- */
export function ConfirmDialog({ open, title, message, onConfirm, onCancel, confirmLabel = 'Confirm', danger = false }) {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <Card style={{ maxWidth: '400px', width: '90%' }}>
        <h3 style={{ margin: '0 0 8px' }}>{title}</h3>
        <p style={{ color: theme.colors.textLight, margin: '0 0 20px', fontSize: '14px' }}>{message}</p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>{confirmLabel}</Button>
        </div>
      </Card>
    </div>
  );
}
